import React from 'react';
import { Box, CardContent, Typography, Collapse, Grid, IconButton, Tooltip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Info as InfoIcon } from '@mui/icons-material';
import {
  CollapsibleMaintenanceCard,
  MaintenanceItemCard,
  ExpandMoreStyled,
} from '../ProfileTab.styles';
import { BRAND_COLORS, SEMANTIC_COLORS } from '../../../../styles/theme';
import { useTranslation } from '../../../../i18n';

interface MaintenanceItem {
  key: string;
  display_name: string;
  interval_hours: number;
  time_remaining: number;
  overdue: boolean;
  alerts_suppressed?: boolean;
  hours_since_maintenance?: number;
}

interface MaintenanceScheduleCardProps {
  title: string;
  items: MaintenanceItem[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onItemInfoClick?: (item: MaintenanceItem) => void;
  hasOverdueItems: boolean;
  showManualButton?: boolean;
  onManualDownload?: (item: MaintenanceItem) => void;
  manualAvailable?: (itemKey: string) => boolean;
}

const formatNumber = (num: number | null | undefined, decimals: number = 2): string => {
  if (num === null || num === undefined) return 'N/A';
  return num.toFixed(decimals);
};

export const MaintenanceScheduleCard: React.FC<MaintenanceScheduleCardProps> = ({
  title,
  items,
  expanded,
  onToggleExpanded,
  onItemInfoClick,
  hasOverdueItems,
  showManualButton = false,
  onManualDownload,
  manualAvailable,
}) => {
  const { t } = useTranslation();

  return (
    <CollapsibleMaintenanceCard $overdue={hasOverdueItems}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={onToggleExpanded}
        >
          <Typography variant="h6" sx={{ color: BRAND_COLORS.forestGreen.main }}>
            {title}
          </Typography>
          {hasOverdueItems && !expanded && (
            <Typography
              variant="body2"
              sx={{ ml: 2, color: BRAND_COLORS.yellow.main, fontWeight: 'bold' }}
            >
              {t('maintenanceTab.overdue')}
            </Typography>
          )}
          <ExpandMoreStyled
            $expanded={expanded}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
          >
            <ExpandMoreIcon />
          </ExpandMoreStyled>
        </Box>

        <Collapse in={expanded}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {items.map((item, index) => {
              const isTrackingOnly = item.alerts_suppressed === true;
              const hoursAccumulated = item.hours_since_maintenance ?? 0;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <MaintenanceItemCard $overdue={!isTrackingOnly && item.overdue}>
                    <CardContent>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: SEMANTIC_COLORS.text.secondary, flex: 1 }}
                        >
                          {item.display_name}
                        </Typography>
                        {showManualButton && onManualDownload && manualAvailable?.(item.key) && (
                          <Tooltip title={t('maintenanceTab.viewMaintenanceManual')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onManualDownload(item);
                              }}
                              sx={{ p: 0.5, ml: 1 }}
                            >
                              <InfoIcon
                                sx={{ fontSize: 16, color: BRAND_COLORS.forestGreen.main }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!showManualButton && onItemInfoClick && (
                          <Tooltip title={t('maintenanceTab.viewInspectionInstructions')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onItemInfoClick(item);
                              }}
                              sx={{ p: 0.5, ml: 1 }}
                            >
                              <InfoIcon
                                sx={{ fontSize: 16, color: BRAND_COLORS.forestGreen.main }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {isTrackingOnly ? (
                        <>
                          <Typography
                            variant="h6"
                            sx={{ color: SEMANTIC_COLORS.text.secondary, fontWeight: 'bold' }}
                          >
                            {t('maintenanceTab.trackingOnly')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: SEMANTIC_COLORS.text.secondary }}
                          >
                            {t('maintenanceTab.hrsAccumulated', {
                              hours: formatNumber(Math.max(hoursAccumulated, 0), 1),
                            })}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="h6"
                            sx={{
                              color: item.overdue
                                ? BRAND_COLORS.yellow.main
                                : BRAND_COLORS.forestGreen.main,
                              fontWeight: 'bold',
                            }}
                          >
                            {item.overdue ? t('maintenanceTab.overdue') : t('maintenanceTab.dueIn')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: SEMANTIC_COLORS.text.secondary }}
                          >
                            {item.overdue
                              ? t('maintenanceTab.hrsOverdue', {
                                  hours: formatNumber(Math.abs(item.time_remaining), 1),
                                })
                              : t('maintenanceTab.hrs', {
                                  hours: formatNumber(item.time_remaining, 1),
                                })}
                          </Typography>
                        </>
                      )}
                    </CardContent>
                  </MaintenanceItemCard>
                </Grid>
              );
            })}
          </Grid>
        </Collapse>
      </CardContent>
    </CollapsibleMaintenanceCard>
  );
};
